import { useDisclosure } from "@shared/hooks/useDisclosure";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { UseModelServiceType } from "@shared/services/api/hooks/useModelService";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Button from "@ui-kit/form/Button/Button.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { useTranslation } from "@shared/utils/internationalization";

import VenueBasedEventRepository from "@app-contexts/dashboard/features/events/data/repositories/VenueBasedEventRepository";
import VenueBasedMembershipGroupRepository from "@app-contexts/dashboard/features/memberships/data/repositories/VenueBasedMembershipGroupRepository";
import VenueBasedProductsRepository from "@app-contexts/dashboard/features/products/data/repositories/VenueBasedProductsRepository";
import PublicBaseEventRepository from "@app-contexts/public/features/event-detail/data/repositories/PublicBaseEventRepository";
import PublicBasedProductsRepository from "@app-contexts/public/features/products/data/repositories/PublicBasedProductsRepository";
import PublicBasedMembershipGroupRepository from "@shared/modules/memberships/data/repositories/PublicBasedMembershipGroupRepository";

import { ProductTypeSerializer } from "@app-contexts/public/features/products/data/types/products";
import { useMemo } from "react";
import ItemGroupAssignedSeats from "../ItemGroupAssignedSeats/ItemGroupAssignedSeats.web";
import useItemGroupPackage from "../hooks/useItemGroupPackage";
import { ItemGroupProductSelection } from "./ItemGroupProductSelection";

export default function ItemGroupPackage({
  packageChildItems,
  packageChildQuantity,
  eventService,
  membershipService,
  productService,
}: {
  packageChildItems: Array<TicketItemGroupType>;
  packageChildQuantity: number;
  eventService: () => UseModelServiceType<
    PublicBaseEventRepository | VenueBasedEventRepository
  >;
  membershipService: () => UseModelServiceType<
    PublicBasedMembershipGroupRepository | VenueBasedMembershipGroupRepository
  >;
  productService: () => UseModelServiceType<
    VenueBasedProductsRepository | PublicBasedProductsRepository
  >;
}) {
  const { t } = useTranslation("common");
  const first3Items = packageChildItems.slice(0, 3);
  const remainingItems =
    packageChildItems?.length > 3 ? packageChildItems.slice(3) : [];

  const { isOpen, onToggle } = useDisclosure();
  const lastId = packageChildItems[packageChildItems.length - 1].id;

  return (
    <Flex
      direction="column"
      border="1px solid"
      borderColor="gray.400"
      borderRadius="sm"
      pb="md"
    >
      <Flex
        backgroundColor="gray.200"
        p="10px 16px"
        justifyContent="space-between"
        borderRadius="sm"
      >
        <Text fontWeight="bold" fontSize="sm">
          {t("Package detail")}
        </Text>
        <Text fontSize="sm">
          {t("{{ count }} items", { count: packageChildQuantity })}
        </Text>
      </Flex>
      {first3Items.map((itemGroup) => (
        <ItemGroupPackage.PackageItem
          isLast={itemGroup.id === lastId}
          key={itemGroup.id}
          itemGroup={itemGroup}
          eventService={eventService}
          membershipService={membershipService}
          productService={productService}
        />
      ))}
      {isOpen &&
        remainingItems?.map((itemGroup) => (
          <ItemGroupPackage.PackageItem
            isLast={itemGroup.id === lastId}
            key={itemGroup.id}
            itemGroup={itemGroup}
            eventService={eventService}
            membershipService={membershipService}
            productService={productService}
          />
        ))}
      {remainingItems && remainingItems.length > 0 && (
        <Button.Ghost
          data-testid="item-group-hide-show-package"
          onClick={onToggle}
        >
          {isOpen ? t("Hide package") : t("View package")}
        </Button.Ghost>
      )}
    </Flex>
  );
}

ItemGroupPackage.PackageItem = function ItemGroupPackageItem({
  itemGroup,
  isLast,
  eventService,
  membershipService,
  productService,
}: {
  isLast: boolean;
  itemGroup: TicketItemGroupType;
  eventService: () => UseModelServiceType<
    PublicBaseEventRepository | VenueBasedEventRepository
  >;
  membershipService: () => UseModelServiceType<
    PublicBasedMembershipGroupRepository | VenueBasedMembershipGroupRepository
  >;
  productService: () => UseModelServiceType<
    VenueBasedProductsRepository | PublicBasedProductsRepository
  >;
}) {
  const {
    name,
    subtitle,
    hasSeats,
    seatPermissions,
    locationPermissions,
    isLoading,
    productData,
  } = useItemGroupPackage(
    itemGroup,
    eventService,
    membershipService,
    productService
  );

  const showProductSelection = useMemo(() => {
    return Boolean(productData && itemGroup.parent);
  }, [itemGroup, productData]);

  return (
    <Flex
      borderBottom={isLast ? "none" : "1px solid"}
      justifyContent={showProductSelection ? "stretch" : undefined}
      borderColor="gray.400"
      p="4px 8px"
      mx="md"
      alignItems="center"
      gap="md"
      overflowX="auto"
    >
      <Text>{itemGroup.quantity}</Text>
      <Flex direction="column" width="100%">
        {isLoading && <ItemGroupPackage.Loading />}
        {!isLoading && (
          <>
            <Text
              fontWeight="bold"
              color="font.dark"
              fontSize="sm"
              noOfLines={1}
            >
              {name}
            </Text>
            {!showProductSelection && (
              <Text color="gray.600" fontSize="sm">
                {subtitle}
              </Text>
            )}
          </>
        )}
        {hasSeats && (
          <ItemGroupAssignedSeats
            seatPermissions={seatPermissions}
            locationPermissions={locationPermissions}
          />
        )}
      </Flex>

      {showProductSelection && (
        <ItemGroupProductSelection
          productData={productData as ProductTypeSerializer}
          itemGroup={itemGroup}
        />
      )}
    </Flex>
  );
};

ItemGroupPackage.Loading = function ItemGroupPackageLoading() {
  return (
    <Flex direction="column" gap="md">
      <Skeleton height="element.md" width="100%" />
      <Skeleton height="element.md" width="100%" />
    </Flex>
  );
};
